import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getPaymentMethod } from '@/models/plans/ports'

// BE uses 'amex' instead of 'american-express'
// We should move to 'amex' at some point
type BaseBrand = 'visa' | 'mastercard' | 'unionpay' | 'unknown'
type CardBrand = BaseBrand | 'american-express'

export type Method = {
  payment_method_id: string
  is_default: boolean
  brand: BaseBrand | 'amex'
  card_number: string
  expiration_date: string
  zip: string
  country: string
  state: string
  city: string
  street1: string
  street2?: string
  last_name: string
  first_name: string
  tax_code?: string
}

export type PaymentMethod = Omit<Partial<Method>, 'brand'> & {
  card_last4?: string
  brand?: CardBrand
}

type Error = {
  message: string
}

const isType = (type?: string): type is CardBrand =>
  typeof type === 'string' &&
  ['visa', 'mastercard', 'american-express', 'unionpay', 'unknown'].indexOf(type) >= 0

const formatType = (type?: string): CardBrand => {
  if (type === 'amex') return 'american-express'
  if (!isType(type)) return 'unknown'
  return type
}

const getLength = (brand?: string) => {
  if (brand === 'american-express') return 15
  if (brand === 'mastercard') return 16
  // Visa can be 13 or 16, but is 16 is more common
  if (brand === 'visa') return 16
  // UnionPay can be 16-19, but is 16 is more common
  if (brand === 'unionpay') return 16
  return 16
}

const getGaps = (length: number) => {
  if (length === 16) return [4, 4, 4, 4]
  if (length === 15) return [4, 6, 5]
  return [4, 4, 4, length - 12]
}

const guessLength = (last4?: string, brand?: string): string => {
  if (!last4) return ''
  const length = getLength(brand)
  const gaps = getGaps(length)
  const placeholder = '*'.repeat(length - 4) + last4
  let value = ''
  let count = 0
  let gapIndex = 0
  for (let i = 0; i < placeholder.length; i++) {
    value = value + placeholder[i]
    count++
    if (gaps[gapIndex] && count >= gaps[gapIndex]) {
      gapIndex++
      count = 0
      value = value + ' '
    }
  }
  return value
}

const getData = async (): Promise<Method[]> => {
  try {
    const result = await getPaymentMethod()
    if (!result) throw new Error('Empty data')
    const cards: Method[] = result.body
    return cards
  } catch (err) {
    throw err
  }
}

const select = (data: Method[]): PaymentMethod => {
  if (!data) return {}
  const card = data.find(method => method.is_default)
  if (!card) return {}
  const { card_number, brand, ...rest } = card
  const cardBrand = formatType(brand)
  const final: PaymentMethod = {
    card_number: guessLength(card_number, cardBrand),
    card_last4: card_number,
    brand: cardBrand,
    ...rest
  }
  return final
}

const usePaymentMethods = (): UseQueryResult<PaymentMethod, Error> =>
  useQuery<Method[], Error, PaymentMethod>({
    queryKey: ['user', 'plans', 'payment', 'default'],
    queryFn: getData,
    staleTime: 30000,
    select
  })

export default usePaymentMethods
